// todo: rename this file to data since it doesn't only contain types

import dayjs from 'dayjs'
import { getBodyDataAttribute } from '../shared/utils'

export type BooleanBitString = '1' | '0'
export type NumericString = `${number}`
export type EquipeScore = NumericString | 'FO' | 'PE' | null

export const URL_COMPETITION_TYPES = [
    'coupe-de-france',
    'national',
    'regional',
    'departemental',
    'interligue',
    'inter-comite',
] as const

export const COMPETITION_TYPES = [
    'COUPE_DE_FRANCE',
    'NATIONAL',
    'REGIONAL',
    'DEPARTEMENTAL',
    'INTER_LIGUES',
    'INTER_COMITES',
] as const

export type UrlCompetitionType = (typeof URL_COMPETITION_TYPES)[number]

export type Saison = {
    id: string
    ext_saisonId: string
    libelle: string
    administrative: BooleanBitString
    sportive: BooleanBitString
    displayResults: BooleanBitString
}

export type Structure = {
    id: string
    ext_structureId: string
    libelle: string
    code: string
    sigle: string
    type: 'LIG' | 'FED' | 'CLU' | 'COM'
    logo: string
    oldUrl: string
    libelleCourt: string
}

export type Competition = {
    id: string
    ext_competitionId: string
    saisonId: string
    structureId: string
    libelle: string
    genre: string
    type: (typeof COMPETITION_TYPES)[number]
    logo: string | null
}

export type Phase = {
    id: string
    ext_phaseId: string
    competitionId: string
    libelle: string
}

export type Poule = {
    id: string
    ext_pouleId: string
    phaseId: string
    libelle: string
    journees: string
    dateDernierUpdateEnfants: string
}

export type Equipe = {
    id: string
    ext_equipeId: string
    pouleId: string
    structureId: string
    libelle: string
    logoActif: BooleanBitString
    ext_structureId?: number
    logo?: string
}

export type ClassementPopulated = {
    id: string
    equipeId: string
    pouleId: string
    place: number
    point: number
    butMoins: number
    butPlus: number
    diff: number
    gagne: number
    perdu: number
    nul: number
    joue: number
    dernieresRencontres: string
    structure_logo: string
    equipe_logoActif: BooleanBitString
    equipe_libelle: string
    ext_structureId: number
}

type Journee = {
    journee_numero: number
    date_debut: string
    date_fin: string
}

export type Rencontre = {
    arbitre1: string | null
    arbitre2: string | null
    arbitre1Id: string | null
    arbitre2Id: string | null
    date: string | null
    // dateDernierUpdateEnfants: string
    equipe1Id: string
    equipe2Id: string
    equipe1Score: EquipeScore
    equipe2Score: EquipeScore
    equipe1ScoreMT: string | null
    equipe2ScoreMT: string | null
    ext_rencontreId: string
    fdmCode: string | null
    id: string
    journeeNumero: string
    pouleId: string
}

export type StatsJoueurs = {
    id: string
    rencontreId: string
    equipeId: string
    ext_statsJoueurId: NumericString
    individuId: NumericString
    licenceId: NumericString
    nom: string
    prenom: string
    numero: NumericString
    buts: NumericString
    arrets: NumericString
    septMetres: NumericString
    deuxMins: NumericString
    tirs: NumericString
    avertissements: BooleanBitString
    disqualifie: BooleanBitString
}

export type Equipement = {
    id: string
    libelle: string
    rue: string
    codePostal: string
    ville: string
    latitude: string | null
    longitude: string | null
}

export type Rematch = {
    id: NumericString
    thumbnailUrl: string
    videoId: string
    // fdmCode: string
    // date: string
}

export const getJournees = (
    poule: Poule,
    options?: { sortBy?: 'journee_numero' | 'date_fin'; sortOrder?: 'ASC' | 'DESC' },
): Journee[] => {
    try {
        const journees = JSON.parse(poule.journees) as Journee[]

        const { sortBy, sortOrder } = options ?? {}

        const sortCoeff = sortOrder === 'DESC' ? -1 : 1

        switch (sortBy) {
            case 'journee_numero': {
                // inline sort journees, but ok since it's from JSON.parse
                return journees.sort(
                    (a, b) => sortCoeff * (a.journee_numero < b.journee_numero ? -1 : 1),
                )
            }
            case 'date_fin': {
                // inline sort journees, but ok since it's from JSON.parse
                return journees.sort(
                    (a, b) => sortCoeff * (dayjs(a.date_fin).isBefore(dayjs(b.date_fin)) ? -1 : 1),
                )
            }
            default: {
                return journees
            }
        }
    } catch (e) {
        return []
    }
}

export const COMPETITION_TYPE_URL_MAPPING: { [x in Competition['type']]: UrlCompetitionType } = {
    COUPE_DE_FRANCE: 'coupe-de-france',
    NATIONAL: 'national',
    REGIONAL: 'regional',
    DEPARTEMENTAL: 'departemental',
    INTER_LIGUES: 'interligue',
    INTER_COMITES: 'inter-comite',
}

export const COMPETITION_TYPE_LABEL: {
    [x in UrlCompetitionType]: { plural: string; singular: string }
} = {
    'coupe-de-france': { plural: 'Coupes de France', singular: 'Coupe de France' },
    national: { plural: 'Nationaux', singular: 'National' },
    regional: { plural: 'Régionaux', singular: 'Régional' },
    departemental: { plural: 'Départementaux', singular: 'Départemental' },
    interligue: { plural: 'Interligues', singular: 'Interligue' },
    'inter-comite': { plural: 'Intercomités', singular: 'Intercomité' },
}

export type SearchType = 'competition' | 'structure' | 'joueur'

export const deduceWinner = ({
    home,
    away,
}: {
    home: number | EquipeScore
    away: number | EquipeScore
}): 'home' | 'away' | 'none' => {
    const h = Number(home)
    const a = Number(away)
    const isHomeInteger = home !== null && Number.isInteger(h)
    const isAwayInteger = away !== null && Number.isInteger(a)

    if (isHomeInteger && isAwayInteger) {
        if (h > a) return 'home'
        if (a > h) return 'away'
        return 'none'
    }

    if (isHomeInteger) return 'home'
    if (isAwayInteger) return 'away'
    return 'none'
}

const PAGE_TYPES = [
    'tunnel',
    'competition',
    'classement',
    'statistiques',
    'recherche',
    'actualites',
    'rencontre',
] as const

export type PageType = (typeof PAGE_TYPES)[number]

export type CachedUserCompetition = Competition & {
    structureLibelle: string
    extSaisonId: string
};
export type CachedUserPoule = CachedUserCompetition & {
    ext_pouleId: string;
    pouleLibelle: string;
    phaseLibelle: string;
}

export const isCachedUserPoule = (item: CachedUserCompetition | CachedUserPoule): item is CachedUserPoule => {
    return 'ext_pouleId' in item
}


// todo: move to utils
export const getCompetitionPageType = (): PageType => {
    const attribute = getBodyDataAttribute('data-competition_page_type', 'competition')
    return (PAGE_TYPES as Readonly<string[]>).includes(attribute)
        ? (attribute as PageType)
        : 'competition'
}

// todo: move to utils
export const retrieveExtId = (seoPathSegment: string): number => {
    const split = seoPathSegment.split('-')
    const lastSegment = split[split.length - 1]
    return parseInt(lastSegment)
}
